// WhyPortugalPR.js
import React from 'react';
import { motion } from 'framer-motion';
import { Globe, Heart, Briefcase, GraduationCap, Shield, TrendingUp } from 'lucide-react';

const WhyPortugalPR = () => {
  const benefits = [
    {
      icon: Globe,
      title: 'Gateway to Europe',
      description: 'Portuguese residency provides access to the Schengen Area (26 European countries). After 5 years, eligible for citizenship and EU passport with visa-free travel to 188 countries.'
    },
    {
      icon: Heart,
      title: 'Exceptional Lifestyle',
      description: 'Mediterranean climate, beautiful coastlines, rich culture, and affordable cost of living. Portugal consistently ranks among Europe\'s safest countries with excellent quality of life.'
    },
    {
      icon: Briefcase,
      title: 'Business-Friendly Environment',
      description: 'Attractive tax regime including Non-Habitual Resident (NHR) program offering tax benefits for 10 years. Growing startup ecosystem and digital nomad-friendly policies.'
    },
    {
      icon: GraduationCap,
      title: 'Quality Education & Healthcare',
      description: 'Access to Portuguese public education and healthcare systems. International schools available. EU-standard healthcare with affordable private options.'
    },
    {
      icon: Shield,
      title: 'Flexible Residency Requirements',
      description: 'Golden Visa requires only 7 days per year in Portugal. D7 Visa has minimal stay requirements. Family reunification available for spouse and dependent children.'
    },
    {
      icon: TrendingUp,
      title: 'Path to EU Citizenship',
      description: 'Eligible for Portuguese citizenship after 5 years of residency. Portugal allows dual citizenship. Portuguese passport provides full EU citizenship rights.'
    }
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-[#202356] mb-4">
            Why Choose Portugal for Residency?
          </h2>
          <p className="text-gray-600 text-base font-light max-w-3xl mx-auto">
            Portugal offers attractive residency programs combining European access, lifestyle benefits, and flexible requirements for investors, retirees, and remote workers.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-[#F5F8FA] p-6 rounded-lg hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-[#202356] to-[#2542D4] rounded-lg flex items-center justify-center mb-4">
                  <Icon size={24} className="text-white" />
                </div>
                <h3 className="text-lg font-semibold text-[#202356] mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 text-sm font-light leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-6"
        >
          <p className="text-gray-700 text-sm font-light leading-relaxed text-center">
            <strong>Did you know?</strong> Portugal's Golden Visa program is one of Europe's most popular residency-by-investment programs, with over 11,000 main applicants approved since 2012. Portuguese residency provides a pathway to EU citizenship and full European Union rights.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyPortugalPR;
